import { Component, Input } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { StatusSeverity } from '../../utility';

export interface ProfileProperty {
	label: string;
	value: string | number | null | undefined;
	htmlValue?: string;
	colSize?: string;
	/** DOVE-2180 — renders `value` as an in-app link (routerLink) instead of raw HTML/text. */
	routerLink?: any[] | string;
	/** Optional `pi pi-*` icon shown before the value when `routerLink` is set. */
	icon?: string;
	/**
	 * DOVE-2341 — inline mode only: force this property onto a new row, so the
	 * consumer can group fields into meaningful rows (e.g. physical stats on one
	 * line, everything else on the next) rather than relying on width-based wrap.
	 */
	breakBefore?: boolean;
	/**
	 * DOVE-2476 — inline mode only: a small p-tag shown right after the value (e.g. the
	 * patient's status beside the Patient ID on the Order Profile). Omit it and the property
	 * renders as before.
	 */
	badge?: { value: string; severity: StatusSeverity } | null;
}

@Component({
	selector: 'app-profile-section',
	templateUrl: './profile-section.component.html',
	styleUrls: ['./profile-section.component.scss']
})
export class ProfileSectionComponent {
	@Input() title: string = '';
	@Input() titleHtml: string = ''; // Optional HTML title (takes precedence over title)
	@Input() properties: ProfileProperty[] = [];
	@Input() actionItems: MenuItem[] = [];
	/**
	 * DOVE-2405 — `styleClass` is optional and forwarded straight to the p-button, so a
	 * consumer can emphasise one action without this component knowing what the emphasis
	 * means. Existing callers that omit it are unchanged.
	 */
	@Input() additionalButtons: {
		label: string;
		icon?: string;
		disabled?: boolean;
		styleClass?: string;
		action: () => void;
	}[] = [];
	@Input() thumbnail: string | null = null;
	@Input() defaultImage: string = '';

	// DOVE-2340 — profile-header reskin. All four are opt-in and default to
	// off/empty, so profiles that don't set them render exactly as before.
	/** Initials circle shown to the left (person profiles only — Patient, User). */
	@Input() avatarInitials?: string | null;
	/** Rendered as a pill next to the title (e.g. a patient or order id). */
	@Input() idBadge?: string | number | null;
	/** Rendered as a status tag next to the title; pulls status out of the grid. */
	@Input() status?: string | null;
	/** p-tag severity for the status tag; the consumer maps its own statuses. */
	@Input() statusSeverity?: 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast';
	/** Render the attributes as one inline row instead of the label-above grid. */
	@Input() inlineProperties: boolean = false;

	showPreview = false;

	onImageError(event: any) {
		event.target.src = this.defaultImage;
	}

	trackByLabel(_index: number, property: ProfileProperty): string {
		return property.label;
	}
}
